// 확장자 칩 입력 — 추가(Enter/쉼표/blur), 삭제(✕/Backspace), 중복/공백 방어
import { useRef, useState } from 'react';

interface Props {
  id?: string;
  tags: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function TagInput({ id, tags, onChange, placeholder = '입력 후 Enter', disabled }: Props) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function addTag() {
    const raw = value.trim().toLowerCase();
    // 최소 2자 이상 + 점으로 시작해야 유효한 확장자로 인정
    if (!raw || raw === '.') { setValue(''); return; }
    if (tags.includes(raw)) { setValue(''); return; }
    onChange([...tags, raw]);
    setValue('');
  }

  function removeTag(idx: number) {
    onChange(tags.filter((_, i) => i !== idx));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && value === '') {
      if (tags.length > 0) onChange(tags.slice(0, -1));
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 5,
        background: 'var(--bg)',
        border: '1px solid var(--border2)',
        borderRadius: 6,
        padding: '6px 10px',
        minHeight: 38,
        alignItems: 'center',
        cursor: 'text',
      }}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, i) => (
        <span
          key={tag}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'var(--surface3)',
            border: '1px solid var(--border2)',
            borderRadius: 4,
            padding: '1px 6px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12,
            color: 'var(--text)',
          }}
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeTag(i)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text3)',
                fontSize: 13,
                lineHeight: 1,
                padding: '0 1px',
              }}
              aria-label={`${tag} 제거`}
            >
              ✕
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value.replace(',', ''))}
        onKeyDown={onKeyDown}
        onBlur={addTag}
        placeholder={tags.length === 0 ? placeholder : ''}
        disabled={disabled}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 12,
          color: 'var(--text)',
          flex: 1,
          minWidth: 100,
        }}
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
}
